import { isPlatformBrowser } from "@angular/common";
import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import { Observable, of } from "rxjs";
import { ContentPopupsPort } from "../../../app/ports/content/content-popups.port";
import {
  ContentCampaign,
  ContentCampaignRequest,
} from "../../../domain/models/content/popup.model";

@Injectable({ providedIn: "root" })
export class LocalContentPopupsPortAdapter implements ContentPopupsPort {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storageKey = "klab.admin.content-popups.v1";

  list(): Observable<ContentCampaign[]> {
    return of(this.read().map((item) => this.copy(item)));
  }

  create(request: ContentCampaignRequest): Observable<ContentCampaign> {
    const items = this.read();
    const now = new Date().toISOString();
    const created: ContentCampaign = {
      ...request,
      id: items.reduce((max, item) => Math.max(max, item.id), 0) + 1,
      redirectUrl: request.redirectUrl?.trim() || null,
      active: request.active ?? true,
      createdAt: now,
      updatedAt: now,
    };
    this.write([...items, created]);
    return of(this.copy(created));
  }

  update(id: number, request: ContentCampaignRequest): Observable<ContentCampaign> {
    const items = this.read();
    const current = this.requireItem(items, id);
    const updated: ContentCampaign = {
      ...current,
      ...request,
      id,
      redirectUrl: request.redirectUrl?.trim() || null,
      active: request.active ?? current.active,
      updatedAt: new Date().toISOString(),
    };
    this.write(items.map((item) => item.id === id ? updated : item));
    return of(this.copy(updated));
  }

  setActive(id: number, active: boolean): Observable<ContentCampaign> {
    const items = this.read();
    const current = this.requireItem(items, id);
    const updated: ContentCampaign = {
      ...current,
      active,
      updatedAt: new Date().toISOString(),
    };
    this.write(items.map((item) => item.id === id ? updated : item));
    return of(this.copy(updated));
  }

  delete(id: number): Observable<void> {
    const items = this.read();
    this.requireItem(items, id);
    this.write(items.filter((item) => item.id !== id));
    return of(void 0);
  }

  private read(): ContentCampaign[] {
    if (!isPlatformBrowser(this.platformId)) return this.seed();

    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return this.seed();

    try {
      const parsed = JSON.parse(raw) as ContentCampaign[];
      return Array.isArray(parsed) ? parsed : this.seed();
    } catch {
      return this.seed();
    }
  }

  private write(items: ContentCampaign[]): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(this.storageKey, JSON.stringify(items));
  }

  private requireItem(items: ContentCampaign[], id: number): ContentCampaign {
    const item = items.find((candidate) => candidate.id === id);
    if (!item) throw new Error("No se encontró el pop up seleccionado");
    return item;
  }

  private copy(item: ContentCampaign): ContentCampaign {
    return { ...item, displaySections: [...item.displaySections] };
  }

  private seed(): ContentCampaign[] {
    const createdAt = "2026-01-01T08:00:00.000Z";
    return [
      {
        id: 1,
        title: "Modal Navidad",
        startAt: "2026-10-15T08:00:00",
        endAt: "2026-10-31T23:59:59",
        displaySections: ["HOME"],
        redirectUrl: null,
        imageDataUrl: this.banner(
          "#0876dc",
          "#092a73",
          "¡Nuevas funciones disponibles!",
        ),
        imageName: "modal-navidad.svg",
        active: true,
        createdAt,
        updatedAt: createdAt,
      },
      {
        id: 2,
        title: "Evento Corporativo",
        startAt: "2026-10-10T08:00:00",
        endAt: "2026-11-06T23:59:59",
        displaySections: ["HOME"],
        redirectUrl: null,
        imageDataUrl: this.banner(
          "#ff7a00",
          "#b41524",
          "Evento corporativo Klab*",
        ),
        imageName: "evento-corporativo.svg",
        active: false,
        createdAt,
        updatedAt: createdAt,
      },
    ];
  }

  private banner(from: string, to: string, text: string): string {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="960" height="480" viewBox="0 0 960 480"><defs><linearGradient id="g"><stop stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient></defs><rect width="960" height="480" rx="28" fill="url(#g)"/><circle cx="790" cy="110" r="155" fill="#fff" opacity=".12"/><circle cx="130" cy="420" r="190" fill="#fff" opacity=".09"/><text x="60" y="205" fill="#fff" font-family="Arial" font-size="50" font-weight="700">Klab*</text><text x="60" y="285" fill="#fff" font-family="Arial" font-size="38">${text}</text></svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }
}
